/**
 * Scénarios pédagogiques pré-définis.
 * Chaque scénario fixe la date, active des couches et positionne la caméra
 * pour donner un point de vue immédiat sur un événement astronomique réel.
 *
 * Tous les ISO dates sont à 12:00 UT (midi) pour stabilité numérique.
 */

import type { LayerId } from '../store/layersStore';

export type ZoomLevel = 'inner' | 'outer' | 'dwarf' | 'far' | 'planet9';

export interface Scenario {
  id: string;
  emoji: string;
  label: string;
  description: string;
  /** Date ISO à appliquer à la timeline. */
  date: string;
  /** Couches à activer/désactiver (override partiel de l'état courant). */
  layers: Partial<Record<LayerId, boolean>>;
  /** Niveau de zoom suggéré pour la caméra. */
  zoom: ZoomLevel;
}

export const ZOOM_DISTANCE: Record<ZoomLevel, number> = {
  inner: 5,    // Mercure → Mars (4 UA en nested ≈ 5 unités)
  outer: 35,   // Jupiter → Neptune
  dwarf: 90,   // Pluton, Éris (~40-70 UA)
  far: 220,    // Sednoids, ETNOs jusqu'à ~500 UA
  planet9: 600, // Orbites Planet 9 (380-800 UA)
};

export const SCENARIOS: Scenario[] = [
  {
    id: 'now',
    emoji: '🪐',
    label: 'Maintenant',
    description: 'Système solaire à la date du jour. Vue par défaut.',
    date: new Date().toISOString().slice(0, 10),
    layers: { planets: true, dwarfs: false, etnos: false, planet9: false },
    zoom: 'outer',
  },
  {
    id: 'neptune-discovery',
    emoji: '🔭',
    label: 'Découverte de Neptune (1846)',
    description: 'Le 23 septembre 1846 : Galle observe Neptune pour la première fois, à moins d\'1° de la position prédite par Le Verrier.',
    date: '1846-09-23',
    layers: { planets: true, dwarfs: false, etnos: false, planet9: false },
    zoom: 'outer',
  },
  {
    id: 'voyager2-uranus',
    emoji: '🛰️',
    label: 'Voyager 2 frôle Uranus (1986)',
    description: 'Le 24 janvier 1986 : seule visite humaine d\'Uranus à ce jour. Voyager 2 passe à 81 500 km de la planète.',
    date: '1986-01-24',
    layers: { planets: true, dwarfs: false, etnos: false, planet9: false },
    zoom: 'outer',
  },
  {
    id: 'pluto-perihelion',
    emoji: '🌌',
    label: 'Pluton au périhélie (1989)',
    description: 'Le 5 septembre 1989 : Pluton à seulement 29,7 UA du Soleil — pendant 20 ans, plus proche que Neptune.',
    date: '1989-09-05',
    layers: { planets: true, dwarfs: true, etnos: false, planet9: false },
    zoom: 'dwarf',
  },
  {
    id: 'sedna-discovery',
    emoji: '🧊',
    label: 'Découverte de Sedna (2003)',
    description: 'Le 14 novembre 2003 : Mike Brown découvre Sedna à 89 UA. Premier sednoïde, son orbite étrange ouvre la voie à l\'hypothèse Planet 9.',
    date: '2003-11-14',
    layers: { planets: true, dwarfs: false, etnos: true, planet9: false },
    zoom: 'far',
  },
  {
    id: 'planet9-hypothesis',
    emoji: '❓',
    label: 'Hypothèse Planet 9 (2016)',
    description: 'Le 20 janvier 2016 : Brown & Batygin publient l\'hypothèse Planet 9 pour expliquer le clustering des ETNOs. 200 orbites candidates affichées.',
    date: '2016-01-20',
    layers: { planets: true, dwarfs: false, etnos: true, planet9: true },
    zoom: 'planet9',
  },
  {
    id: 'new-horizons-pluto',
    emoji: '🛸',
    label: 'New Horizons survole Pluton (2015)',
    description: 'Le 14 juillet 2015 : New Horizons passe à 12 500 km de Pluton, livrant les premières images détaillées.',
    date: '2015-07-14',
    layers: { planets: true, dwarfs: true, etnos: false, planet9: false },
    zoom: 'dwarf',
  },
  {
    id: 'ammonite',
    emoji: '🟣',
    label: 'Ammonite (2023 KQ14) annoncée (2025)',
    description: '14 juillet 2025 : annonce de "Ammonite", un objet sedna-like dont l\'orbite ne s\'aligne PAS avec les autres — défi pour le modèle Planet 9 simple.',
    date: '2025-07-14',
    layers: { planets: true, dwarfs: false, etnos: true, planet9: true },
    zoom: 'planet9',
  },
];
